/**
 * A switch.
 *
 * `role="switch"` with `aria-checked` rather than a styled checkbox, because a
 * screen reader should announce "on/off", not "checked". The label is a real
 * `<label>` bound to the control, so the whole row is a hit target.
 *
 * The state is never carried by colour alone: the knob moves, and the row keeps
 * its label. That is the same rule the status chips follow.
 */

import { useId } from 'react';
import type { JSX, ReactNode } from 'react';

export interface ToggleProps {
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly label: ReactNode;
  readonly hint?: ReactNode;
  readonly disabled?: boolean | undefined;
}

export function Toggle({
  checked,
  onChange,
  label,
  hint,
  disabled = false,
}: ToggleProps): JSX.Element {
  const id = useId();

  return (
    <div className="between" style={{ gap: 12, alignItems: 'flex-start' }}>
      <label htmlFor={id} style={{ cursor: disabled ? 'not-allowed' : 'pointer', flex: 1 }}>
        <span className="body-sm">{label}</span>
        {hint !== undefined && <p className="caption">{hint}</p>}
      </label>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        className="sw"
        data-on={checked ? 'true' : 'false'}
        onClick={() => {
          onChange(!checked);
        }}
      >
        <span className="sw-knob" />
      </button>
    </div>
  );
}
